import type { Awaitable } from "@alaffia-technology-solutions/types";

import type { __internal__ } from "@foxitsoftware/foxit-pdf-sdk-for-web-library/lib";

import type {
  PDFAnnot,
  PDFBookmark,
  PDFFoxitRect,
  PDFHighlightCoords,
  PDFPoint,
  PDFViewMode,
} from "../../helper-types";
import type { PDFViewCtrl } from "../../loaders/foxit-pdf-view-ctrl";

type EnumValues<T> = T[keyof T];

export const pdfViewerCustomEvents = {
  pdfViewerOpenSuccess: "pdf-viewer-open-success",
} as const;

export type PDFViewerEvents = typeof PDFViewCtrl.constants.ViewerEvents &
  typeof pdfViewerCustomEvents;

export interface PDFControllerSlice {
  pdfViewer: __internal__.PDFViewer | null;
  pdfUrl: null | string;
  pageCount: number | null;
  currentPageIndex: number | null;
  scale: number | "fitWidth" | "fitHeight";
  error: string | null;
  bookmarks: PDFBookmark[] | null;
  id: string | null;
  state: "closed" | "initialized" | "opened" | "error";
  viewMode: PDFViewMode | null;
  events: PDFViewerEvents | null;
  init: (el: HTMLElement) => void;
  open: (url: string, id: string) => Promise<void>;
  close: () => void;
  nextPage: () => Promise<void>;
  prevPage: () => Promise<void>;
  getTextRect: (
    pageIndex: number,
    text: string,
  ) => Promise<PDFFoxitRect | null>;
  jumpToPage: (pageIndex: number, offset?: PDFPoint) => Promise<void>;
  setCurrentPageIndex: (pageIndex: number) => void;
  getPageThumbnail: (pageIndex: number) => Promise<HTMLImageElement>;
  loadBookmarks: () => Promise<PDFBookmark[] | null>;
  rotate: (direction: "left" | "right") => Promise<void>;
  zoom: (step: Parameters<PDFViewCtrl.PDFViewer["zoomTo"]>[0]) => Promise<void>;
  addEventListener: <
    T extends EnumValues<typeof PDFViewCtrl.constants.ViewerEvents> | undefined,
  >(
    eventName: T,
    listener: Parameters<
      ReturnType<PDFViewCtrl.PDFViewer["getEventEmitter"]>["addListener"]
    >[1],
  ) => void;
  removeEventListener: <
    T extends EnumValues<typeof PDFViewCtrl.constants.ViewerEvents> | undefined,
  >(
    eventName: T,
    listener: Parameters<
      ReturnType<PDFViewCtrl.PDFViewer["getEventEmitter"]>["removeListener"]
    >[1],
  ) => void;
  highlightTextOnPage: (
    pageIndex: number,
    stringsToHighlight: string[],
  ) => Awaitable<__internal__.Annot[]>;
  removeHighlights: (highlights: __internal__.Annot[]) => Awaitable<void>;
  removeHighlightsOnPage: (pageIndex: number) => Awaitable<void>;
  addHighlight: (
    pageIndex: number,
    highlightCoords: PDFHighlightCoords[],
    options?: {
      color?: string;
    },
  ) => Promise<PDFAnnot>;
  getPageDimensions: (
    pageIndex: number,
  ) => Promise<{ width: number; height: number }>;
  setViewMode: (viewMode: PDFViewMode) => void;
}
